import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import EditableContent from './EditableContent';
import grasklippningImage from '../assets/images/grasklippning.jpg';
import tradfallningImage from '../assets/images/tradfallning.jpg';
import fasadImage from '../assets/images/fasad.jpg';
import config from '../config/config';

// Standardinnehåll
const defaultContent = {
  gardening_description: "Vi hjälper dig att skapa och underhålla en vacker och funktionell utemiljö, oavsett om det gäller en privat trädgård eller företagets grönområden.",
  gardening_services: `<ul>
    <li>Trädbeskäring och trädfällning</li>
    <li>Häckklippning</li>
    <li>Anläggning av gräs och plantering</li>
    <li>Stenläggning och murar</li>
    <li>Löpande skötsel och underhåll</li>
    <li>Övriga trädgårdstjänster</li>
  </ul>`,
  construction_description: "Vi tar oss an byggprojekt i alla storlekar med fokus på kvalitet och kundnöjdhet. Oavsett om det gäller renovering, ombyggnad eller nyproduktion strävar vi efter hållbara och genomtänkta lösningar.",
  construction_services: `<ul>
    <li>Nybyggnationer</li>
    <li>Trall och altanbygge</li>
    <li>Förrådsbyggnationer</li>
    <li>Pergola och lusthus</li>
    <li>Målning och tapetsering</li>
    <li>Övriga byggprojekt</li>
  </ul>`,
  preservation_description: "Vi brinner för att bevara och återställa äldre byggnader med omsorg och respekt för historien. Med fokus på traditionella tekniker och genuint hantverk hjälper vi dig att ge nytt liv åt tidstypiska miljöer.",
  preservation_services: `<ul>
    <li>Fönsterrenovering</li>
    <li>Fasadrestaurering</li>
    <li>Timmerhusrenovering</li>
    <li>Husgrundsarbeten</li>
    <li>Konsultation</li>
    <li>Övriga byggtjänster</li>
  </ul>`
};

const Services = () => {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${config.api.baseUrl}/cms/pages/services`);
        if (!response.ok) {
          throw new Error('Kunde inte hämta innehåll');
        }
        
        const data = await response.json();
        if (data.content) {
          const fetchedContent = JSON.parse(data.content);
          setContent(prevContent => ({
            ...prevContent,
            ...fetchedContent
          }));
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };

    fetchContent();
  }, []);

  const handleContentUpdate = (key: string, newContent: string) => {
    setContent(prevContent => ({
      ...prevContent,
      [key]: newContent
    }));
  };

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center" 
          sx={{ 
            mb: 6,
            mt: { xs: 4, sm: 8 },
            fontSize: {
              xs: '2rem',    // Mobil
              sm: '2.5rem',  // Tablet
              md: '3rem'     // Desktop
            },
            px: { xs: 2, sm: 0 }
          }}
        >
          Våra Tjänster
        </Typography>
        
        {/* Trädgårdsarbete */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{
                fontSize: {
                  xs: '1.75rem',  // Mobil
                  sm: '2rem',     // Tablet
                  md: '2.25rem'   // Desktop
                },
                px: { xs: 2, sm: 0 }
              }}
            >
              Trädgårdsarbete
            </Typography>
            <EditableContent
              content={content.gardening_description}
              pageName="services"
              contentKey="gardening_description"
              type="text"
              onUpdate={(newContent) => handleContentUpdate('gardening_description', newContent)}
            />
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                mt: 2,
                px: { xs: 2, sm: 0 }
              }}
            >
              Våra tjänster inkluderar:
            </Typography>
            <Box sx={{ px: { xs: 2, sm: 0 } }}>
              <EditableContent
                content={content.gardening_services}
                pageName="services"
                contentKey="gardening_services"
                type="text"
                onUpdate={(newContent) => handleContentUpdate('gardening_services', newContent)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 300, maxHeight: 400 }}>
              <EditableContent
                content=""
                pageName="services"
                contentKey="gardening_image"
                type="image"
                imageUrl={grasklippningImage}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Byggprojekt */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 300, maxHeight: 400 }}>
              <EditableContent
                content=""
                pageName="services"
                contentKey="construction_image"
                type="image"
                imageUrl={fasadImage}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <Typography variant="h4" gutterBottom>
              Byggprojekt
            </Typography>
            <EditableContent
              content={content.construction_description}
              pageName="services"
              contentKey="construction_description"
              type="text"
              onUpdate={(newContent) => handleContentUpdate('construction_description', newContent)}
            />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Vi erbjuder:
            </Typography>
            <EditableContent
              content={content.construction_services}
              pageName="services"
              contentKey="construction_services"
              type="text"
              onUpdate={(newContent) => handleContentUpdate('construction_services', newContent)}
            />
          </Grid>
        </Grid>

        {/* Byggnadsvård */}
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom>
              Byggnadsvård
            </Typography>
            <EditableContent
              content={content.preservation_description}
              pageName="services"
              contentKey="preservation_description"
              type="text"
              onUpdate={(newContent) => handleContentUpdate('preservation_description', newContent)}
            />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Vi erbjuder:
            </Typography>
            <EditableContent
              content={content.preservation_services}
              pageName="services"
              contentKey="preservation_services"
              type="text"
              onUpdate={(newContent) => handleContentUpdate('preservation_services', newContent)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 300, maxHeight: 400 }}>
              <EditableContent
                content=""
                pageName="services"
                contentKey="preservation_image"
                type="image"
                imageUrl={tradfallningImage}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Services; 